import { useCallback, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { useTranslation } from "react-i18next";

export interface FileDropZoneFieldProps {
  /** Called with the first selected or dropped file */
  onFileSelected: (file: File) => void | Promise<void>;
  disabled?: boolean;
  uploading?: boolean;
  accept?: string;
}

export default function FileDropZoneField({
  onFileSelected,
  disabled = false,
  uploading = false,
  accept,
}: FileDropZoneFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const busy = disabled || uploading;

  const openPicker = useCallback(() => {
    if (busy) return;
    inputRef.current?.click();
  }, [busy]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (file && !busy) {
        void onFileSelected(file);
      }
    },
    [busy, onFileSelected]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (busy) return;
      setIsDragging(true);
    },
    [busy]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      e.dataTransfer.dropEffect = busy ? "none" : "copy";
    }
  }, [busy]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (busy) return;
      const file = e.dataTransfer.files?.[0];
      if (file) {
        void onFileSelected(file);
      }
    },
    [busy, onFileSelected]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPicker();
      }
    },
    [openPicker]
  );

  return (
    <>
      {/* position-based hiding instead of display:none — iOS Safari blocks .click() on display:none inputs */}
      <input
        ref={inputRef}
        type="file"
        {...(accept && accept !== "*/*" ? { accept } : {})}
        style={{ position: "fixed", top: "-100vh", left: "-100vw", width: 0, height: 0, opacity: 0 }}
        onChange={handleInputChange}
        disabled={busy}
        tabIndex={-1}
        aria-hidden
      />
      <Box
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={busy ? -1 : 0}
        aria-disabled={busy}
        aria-label={t("Drop file here or click to browse")}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 2,
          px: 2,
          minHeight: 56,
          borderRadius: 1,
          borderStyle: "dashed",
          borderWidth: 2,
          borderColor: isDragging ? "primary.main" : "divider",
          bgcolor: isDragging ? "action.selected" : "action.hover",
          opacity: busy ? 0.6 : 1,
          cursor: busy ? "not-allowed" : "pointer",
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <UploadFileOutlined color={isDragging ? "primary" : "action"} fontSize="small" />
        <Typography variant="body2" color="text.secondary" component="span" textAlign="center">
          {uploading ? t("Uploading…") : t("Drop file here or click to browse")}
        </Typography>
      </Box>
    </>
  );
}
